<template>
  <div v-if="target" class="move-modal-mask" @click.self="close">
    <div class="modal-dialog" role="dialog" aria-labelledby="moveModalTitle">
      <div class="modal-content isim-rounded overflow-hidden">
        <div class="modal-header">
          <h5 id="moveModalTitle" class="modal-title">{{ title }}</h5>
          <button type="button" class="close" aria-label="Close" @click="close">
            <i class="material-icons">close</i>
          </button>
        </div>

        <div class="modal-body bg-light p-3">
          <p class="mb-2">Choose a destination folder:</p>
          <ul class="destination-list" role="listbox">
            <li
              v-for="option in options"
              :key="option.uuid || 'root'"
              :class="{
                'destination-option': true,
                selected: selectedUuid === option.uuid,
                disabled: option.disabled,
              }"
              :style="{ paddingLeft: 12 + option.depth * 20 + 'px' }"
              role="option"
              :aria-selected="selectedUuid === option.uuid ? 'true' : 'false'"
              :aria-disabled="option.disabled ? 'true' : 'false'"
              :tabindex="option.disabled ? -1 : 0"
              @click="select(option)"
              @keydown.enter.prevent="select(option)"
            >
              <span class="material-icons option-icon">{{
                option.uuid ? "folder" : "home"
              }}</span>
              <span class="option-name">{{ option.name }}</span>
              <small v-if="option.disabledReason" class="option-note">
                {{ option.disabledReason }}
              </small>
              <span
                v-else-if="selectedUuid === option.uuid"
                class="material-icons option-check"
                >check</span
              >
            </li>
          </ul>

          <div class="alert alert-danger mt-3 mb-0" v-if="error">
            {{ error }}
          </div>
        </div>

        <div class="modal-footer">
          <small v-if="!hasSelection" class="text-muted mr-auto">
            Select a folder to move to
          </small>
          <button type="button" class="btn btn-light" @click="close">
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!hasSelection || saving"
            @click="move"
          >
            {{ saving ? "Moving..." : "Move" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { showToast, folderName } from "@/utils/folder.helper";

const STORE = "scenarioDirectoriesStore";

export default {
  name: "MoveToFolderModal",
  data() {
    return {
      // undefined = nothing chosen yet, null = root
      selectedUuid: undefined,
      saving: false,
      error: null,
    };
  },
  computed: {
    ...mapGetters(STORE, {
      target: "moveTarget",
      flatDirectories: "flatDirectories",
    }),
    isDirectory() {
      return this.target.type === "directory";
    },
    scenarioCount() {
      return this.isDirectory ? 0 : this.target.uuids.length;
    },
    title() {
      return this.scenarioCount > 1
        ? `Move ${this.scenarioCount} scenarios`
        : `Move "${this.target.name}"`;
    },
    // The folder the item is in now; null for the root
    currentLocation() {
      if (this.isDirectory) {
        const dir = this.flatDirectories.find((d) => d.uuid === this.target.uuid);
        return dir ? dir.parentUuid : null;
      }
      return this.target.currentDirectoryUuid || null;
    },
    // A folder can't be moved into itself or any of its subfolders
    excludedUuids() {
      if (!this.isDirectory) return new Set();

      const excluded = new Set([this.target.uuid]);
      this.flatDirectories.forEach((d) => {
        if (excluded.has(d.parentUuid)) excluded.add(d.uuid);
      });
      return excluded;
    },
    options() {
      const root = { uuid: null, name: "My Scenarios", depth: 0 };
      const folders = this.flatDirectories.map((d) => ({
        ...d,
        depth: d.depth + 1,
      }));

      return [root, ...folders].map((option) => {
        let disabledReason = null;
        if (this.isDirectory && option.uuid === this.target.uuid) {
          disabledReason = "This folder";
        } else if (this.excludedUuids.has(option.uuid)) {
          disabledReason = "Subfolder";
        } else if (option.uuid === this.currentLocation) {
          disabledReason = "Current location";
        }

        return { ...option, disabledReason, disabled: !!disabledReason };
      });
    },
    hasSelection() {
      return this.selectedUuid !== undefined;
    },
  },
  methods: {
    select(option) {
      if (option.disabled) return;
      this.selectedUuid = option.uuid;
    },
    close() {
      this.$store.commit(`${STORE}/setMoveTarget`, null);
    },
    move() {
      this.saving = true;
      this.error = null;

      const request = this.isDirectory
        ? this.$store.dispatch(`${STORE}/moveDirectory`, {
            uuid: this.target.uuid,
            parentUuid: this.selectedUuid,
          })
        : this.$store.dispatch(`${STORE}/moveScenarios`, {
            uuids: this.target.uuids,
            directoryUuid: this.selectedUuid,
          });

      const what =
        this.scenarioCount > 1
          ? `${this.scenarioCount} scenarios`
          : `"${this.target.name}"`;
      const destination = folderName(this, this.selectedUuid);

      request
        .then(() => {
          showToast(this, `Moved ${what} to ${destination}`);
          this.close();
        })
        .catch((error) => {
          this.error = typeof error === "string" ? error : "Couldn't move";
        })
        .finally(() => (this.saving = false));
    },
  },
};
</script>

<style scoped>
.move-modal-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(244, 244, 244, 0.8);
  z-index: 999;
  overflow-y: auto;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 6vh;
}

.modal-dialog {
  width: 100%;
}

.modal-content {
  border: none;
  box-shadow: 0 12px 28px 0 rgba(0, 0, 0, 0.2);
  border-radius: 8px;
}

.destination-list {
  max-height: 50vh;
  overflow-y: auto;
  list-style: none;
  margin: 0;
  padding: 4px;
  background: #ffffff;
  border: 1px solid #e3e6ea;
  border-radius: 8px;
}

.destination-option {
  display: flex;
  align-items: center;
  height: 36px;
  padding-right: 12px;
  border-radius: 6px;
  color: #212529;
  cursor: pointer;
}

.destination-option:hover,
.destination-option:focus {
  background: rgba(0, 0, 0, 0.04);
  outline: none;
}

.destination-option.selected {
  background: rgba(89, 158, 216, 0.15);
  color: var(--isim-primary);
  font-weight: 600;
}

.destination-option.disabled {
  color: #adb5bd;
  background: transparent;
  cursor: not-allowed;
}

.option-icon {
  font-size: 20px;
  margin-right: 10px;
  color: var(--isim-light-icon);
}

.destination-option.selected .option-icon,
.option-check {
  color: var(--isim-primary);
}

.option-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.option-note {
  margin-left: 8px;
  font-style: italic;
}

.option-check {
  font-size: 20px;
}
</style>
